/**
 * Delivery endpoints. The secret is write-only on the server — a listing never returns
 * it — so this screen shows it once, at creation, and says so plainly rather than
 * offering a "reveal" that could never work.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, post } from "../lib/api";
import { stamp } from "../lib/format";
import type { WebhookList } from "../lib/types";
import { Shell } from "../components/Shell";
import { DataTable } from "../components/DataTable";
import { Button, Chip, Empty, Eyebrow, Failed, Loading, Panel } from "../components/ui";

const MIN_SECRET = 16;

function randomSecret(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function Webhooks() {
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState(randomSecret);
  const [issued, setIssued] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const listing = useQuery({ queryKey: ["webhooks"], queryFn: () => get<WebhookList>("/v1/webhooks") });

  const create = useMutation({
    mutationFn: () => post<unknown>("/v1/webhooks", { url: url.trim(), secret }),
    onSuccess: () => {
      setIssued(secret);
      setUrl("");
      setSecret(randomSecret());
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["webhooks"] });
    },
    onError: (cause) => setError(cause instanceof Error ? cause.message : String(cause)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => del(`/v1/webhooks/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["webhooks"] }),
    onError: (cause) => setError(cause instanceof Error ? cause.message : String(cause)),
  });

  function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    if (!/^https?:\/\/.+/i.test(url.trim())) {
      setError("The URL must start with http:// or https://.");
      return;
    }
    if (secret.length < MIN_SECRET) {
      setError(`The signing secret must be at least ${MIN_SECRET} characters.`);
      return;
    }
    create.mutate();
  }

  const hooks = listing.data?.webhooks ?? [];
  const input =
    "w-full border border-hair bg-ground px-3 py-2 font-mono text-[12px] text-text outline-none transition-colors duration-200 ease-alt placeholder:text-faint focus:border-gold";

  return (
    <Shell fill crumb={<span className="text-text">webhooks</span>}>
      <div className="shrink-0">
        <Eyebrow n="03">delivery</Eyebrow>
        <h2 className="mt-2 text-[1.5rem] leading-none tracking-tighter uppercase">Webhooks</h2>
        <p className="mt-2 max-w-[70ch] text-[12.5px] leading-relaxed text-dim">
          Every endpoint receives <span className="font-mono text-gold">parse.finished</span>,
          signed <span className="font-mono">HMAC-SHA256</span> over{" "}
          <span className="font-mono">&lt;timestamp&gt;.&lt;body&gt;</span> in{" "}
          <span className="font-mono">x-lrd-signature</span>. Three attempts, then the job table
          stays the source of truth — a lost callback never means a lost parse.
        </p>
      </div>

      <Panel title="add an endpoint" bodyClass="shrink-0">
        <form onSubmit={submit} className="flex flex-col gap-4 px-4 py-4">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-2">
              <span className="eyebrow">// endpoint url</span>
              <input
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                placeholder="https://intake.example.com/hooks/parse"
                spellCheck={false}
                className={input}
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="eyebrow">// signing secret</span>
              <div className="flex gap-2">
                <input
                  value={secret}
                  onChange={(event) => setSecret(event.target.value)}
                  spellCheck={false}
                  className={`${input} min-w-0 flex-1`}
                />
                <Button type="button" onClick={() => setSecret(randomSecret())}>
                  new
                </Button>
              </div>
            </label>
          </div>

          <p className="font-mono text-[11px] text-faint">
            &gt; copy the secret now — the server stores it write-only and will never show it again
          </p>

          {error && (
            <p className="border border-crit/40 bg-crit-wash px-3 py-2 text-[12.5px] text-crit">
              {error}
            </p>
          )}
          {issued && (
            <div className="border border-ok/40 bg-ok-wash px-3 py-2">
              <p className="font-mono text-[10.5px] font-bold tracking-[0.16em] text-ok uppercase">
                endpoint added — this is the secret
              </p>
              <p className="mt-1.5 font-mono text-[12px] break-all text-ok">{issued}</p>
            </div>
          )}

          <Button variant="primary" type="submit" disabled={create.isPending} className="self-start">
            {create.isPending ? "adding…" : "add endpoint"}
          </Button>
        </form>
      </Panel>

      <Panel fill title="endpoints" aside={<Chip>{hooks.length}</Chip>}>
        {listing.isPending ? (
          <Loading what="endpoints" />
        ) : listing.isError ? (
          <Failed error={listing.error} what="endpoints" />
        ) : hooks.length === 0 ? (
          <Empty
            title="no endpoints"
            hint="Without one, a client has to poll the document or the job to learn that a parse finished."
          />
        ) : (
          <DataTable
            fill
            table="webhooks"
            rows={hooks}
            rowKey={(hook) => hook.id}
            columns={[
              {
                key: "url",
                header: "url",
                width: 460,
                cellClass: "font-mono text-[12px] break-all",
                render: (hook) => hook.url,
              },
              {
                key: "added",
                header: "added",
                width: 200,
                cellClass: "font-mono text-[11.5px] text-dim",
                render: (hook) => stamp(hook.created_at),
              },
              {
                key: "remove",
                header: "",
                width: 120,
                render: (hook) => (
                  <Button
                    variant="ghost"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(hook.id)}
                    className="hover:!text-crit"
                  >
                    remove
                  </Button>
                ),
              },
            ]}
          />
        )}
      </Panel>
    </Shell>
  );
}
